import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { classFeesAPI, studentAPI } from '../../services/api';

const initialForm = {
  enrollment_no: '',
  student_name: '',
  date_of_birth: '',
  gender: 'MALE',
  standard: '',
  section: 'A',
  caste: '',
  father_name: '',
  mother_name: '',
  parent_phone: '',
  parent_email: '',
  phone_number: '',
  current_address: '',
  permanent_address: '',
  district: '',
  username: '',
  password: '',
  status: 'ACTIVE',
};

const getInitialForm = () => ({
  ...initialForm,
});

const notificationLabels = {
  whatsapp: 'WhatsApp',
  email: 'Email',
};

const buildNotificationStatusText = (channel, result) => {
  const label = notificationLabels[channel] || channel;

  if (result?.sent) {
    return `${label} sent`;
  }

  if (result?.skipped) {
    return `${label} skipped${result.message ? ` (${result.message})` : ''}`;
  }

  return `${label} failed${result?.message ? ` (${result.message})` : ''}`;
};

const buildAdmissionSaveNotice = (notifications) => {
  if (!notifications) {
    return {
      severity: 'success',
      message: 'Student added successfully.',
    };
  }

  const channels = ['whatsapp', 'email'];
  const statuses = channels.map((channel) => buildNotificationStatusText(channel, notifications[channel]));
  const allSent = channels.every((channel) => notifications[channel]?.sent);
  const anyFailed = channels.some((channel) => notifications[channel] && !notifications[channel].sent && !notifications[channel].skipped);

  return {
    severity: allSent ? 'success' : anyFailed ? 'warning' : 'info',
    message: `Student added successfully. ${statuses.join('. ')}.`,
  };
};

const AdminStudents = () => {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [fees, setFees] = useState([]);
  const [vehicleFees, setVehicleFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [successSeverity, setSuccessSeverity] = useState('success');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(getInitialForm());
  const [feeAmounts, setFeeAmounts] = useState({});
  const [vehicleFeeId, setVehicleFeeId] = useState('none');
  const [vehicleFeeAmount, setVehicleFeeAmount] = useState('');
  const [feesEditable, setFeesEditable] = useState(false);

  const loadStudents = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await studentAPI.getAll();
      setStudents(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStudents();
  }, []);

  const loadAdmissionSettings = async () => {
    const [settingsResponse, enrollmentResponse] = await Promise.all([
      classFeesAPI.getSettings(),
      studentAPI.getNextEnrollment(),
    ]);

    setClasses(Array.isArray(settingsResponse.data.classes) ? settingsResponse.data.classes : []);
    setFees(Array.isArray(settingsResponse.data.fees) ? settingsResponse.data.fees : []);
    setVehicleFees(Array.isArray(settingsResponse.data.vehicleFees) ? settingsResponse.data.vehicleFees : []);

    return enrollmentResponse.data?.enrollment_no || '';
  };

  const handleOpen = async () => {
    setForm(getInitialForm());
    setFeeAmounts({});
    setVehicleFeeId('none');
    setVehicleFeeAmount('');
    setFeesEditable(false);
    setError('');
    setSuccess('');
    setSuccessSeverity('success');
    try {
      const enrollmentNo = await loadAdmissionSettings();
      setForm((prev) => ({
        ...prev,
        enrollment_no: enrollmentNo,
      }));
      setOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load admission fee settings');
    }
  };

  const handleClose = () => {
    if (!saving) {
      setOpen(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'standard') {
      const classFees = fees.filter((fee) => fee.standard === value);
      const nextAmounts = {};
      classFees.forEach((fee) => {
        nextAmounts[fee.id] = fee.amount;
      });
      setFeeAmounts(nextAmounts);
    }
  };

  const handleVehicleFeeChange = (event) => {
    const value = event.target.value;
    setVehicleFeeId(value);

    if (value === 'none') {
      setVehicleFeeAmount('');
      return;
    }

    const selected = vehicleFees.find((fee) => String(fee.id) === value);
    setVehicleFeeAmount(selected?.amount || '');
  };

  const handleFeeAmountChange = (feeId, value) => {
    setFeeAmounts((prev) => ({
      ...prev,
      [feeId]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.student_name || !form.standard || !form.date_of_birth || !form.gender) {
      setError('Name, date of birth, gender, and class are required.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      setSuccessSeverity('success');
      const selectedFees = fees
        .filter((fee) => fee.standard === form.standard)
        .map((fee) => ({
          ...fee,
          amount: feeAmounts[fee.id] ?? fee.amount,
        }));
      const selectedVehicleFee = vehicleFeeId === 'none'
        ? null
        : {
            ...vehicleFees.find((fee) => String(fee.id) === vehicleFeeId),
            amount: vehicleFeeAmount,
          };

      const response = await studentAPI.create({
        ...form,
        selected_fees: selectedFees,
        selected_vehicle_fee: selectedVehicleFee,
      });
      const notice = buildAdmissionSaveNotice(response.data?.notifications);
      setSuccessSeverity(notice.severity);
      setSuccess(notice.message);
      setOpen(false);
      setForm(getInitialForm());
      setFeeAmounts({});
      setVehicleFeeId('none');
      setVehicleFeeAmount('');
      setFeesEditable(false);
      await loadStudents();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add student');
    } finally {
      setSaving(false);
    }
  };

  const activeClasses = classes.filter((cls) => Number(cls.is_active) === 1);
  const selectedClassFees = fees.filter((fee) => fee.standard === form.standard);
  const activeVehicleFees = vehicleFees.filter((fee) => Number(fee.is_active) === 1);
  const feeTotal = selectedClassFees.reduce(
    (total, fee) => total + Number(feeAmounts[fee.id] || 0),
    0
  ) + Number(vehicleFeeId === 'none' ? 0 : vehicleFeeAmount || 0);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h5" fontWeight="bold">
          Students
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpen}
          sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
        >
          Add Student
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity={successSeverity} sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : students.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Enrollment No</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Section</TableCell>
                  <TableCell>Caste</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students.map((student) => (
                  <TableRow key={student.id} hover>
                    <TableCell>{student.enrollment_no}</TableCell>
                    <TableCell>{student.student_name}</TableCell>
                    <TableCell>{student.standard}</TableCell>
                    <TableCell>{student.section}</TableCell>
                    <TableCell>{student.caste || '-'}</TableCell>
                    <TableCell>{student.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography color="text.secondary">
            No students found.
          </Typography>
        )}
      </Paper>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle>Add Student</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="enrollment_no"
                  label="Enrollment No"
                  value={form.enrollment_no}
                  InputProps={{ readOnly: true }}
                  helperText="Auto generated"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="student_name"
                  label="Student Name"
                  value={form.student_name}
                  onChange={handleChange}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="date_of_birth"
                  label="Date of Birth"
                  type="date"
                  value={form.date_of_birth}
                  onChange={handleChange}
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  name="gender"
                  label="Gender"
                  value={form.gender}
                  onChange={handleChange}
                  required
                  fullWidth
                >
                  <MenuItem value="MALE">Male</MenuItem>
                  <MenuItem value="FEMALE">Female</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="caste"
                  label="Caste"
                  value={form.caste}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  select
                  name="standard"
                  label="Class"
                  value={form.standard}
                  onChange={handleChange}
                  required
                  fullWidth
                >
                  {activeClasses.map((cls) => (
                    <MenuItem key={cls.id} value={cls.standard}>
                      {cls.display_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  name="section"
                  label="Section"
                  value={form.section}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="father_name"
                  label="Father Name"
                  value={form.father_name}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="mother_name"
                  label="Mother Name"
                  value={form.mother_name}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="parent_phone"
                  label="Parent Phone"
                  value={form.parent_phone}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="phone_number"
                  label="Student Phone"
                  value={form.phone_number}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="parent_email"
                  label="Parent Email"
                  type="email"
                  value={form.parent_email}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="current_address"
                  label="Current Address"
                  value={form.current_address}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="permanent_address"
                  label="Permanent Address"
                  value={form.permanent_address}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="district"
                  label="District"
                  value={form.district}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="username"
                  label="Login Username"
                  value={form.username}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="password"
                  label="Login Password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  spacing={2}
                >
                  <Typography variant="h6">Admission Fees</Typography>
                  <Button type="button" variant="outlined" onClick={() => setFeesEditable((prev) => !prev)}>
                    {feesEditable ? 'Lock Fees' : 'Edit Fees'}
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Class Fees
                  </Typography>
                  {form.standard && selectedClassFees.length > 0 ? (
                    <Stack spacing={1.5}>
                      {selectedClassFees.map((fee) => (
                        <Stack
                          key={fee.id}
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ xs: 'stretch', sm: 'center' }}
                          justifyContent="space-between"
                        >
                          <Typography variant="body2">
                            {fee.fee_type}
                          </Typography>
                          <TextField
                            size="small"
                            type="number"
                            value={feeAmounts[fee.id] ?? fee.amount}
                            onChange={(event) => handleFeeAmountChange(fee.id, event.target.value)}
                            disabled={!feesEditable}
                            inputProps={{ min: 0, step: '0.01' }}
                            sx={{ width: { xs: '100%', sm: 150 } }}
                          />
                        </Stack>
                      ))}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary" variant="body2">
                      Select a class to show fees added by admin.
                    </Typography>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <FormControl fullWidth>
                    <FormLabel>Vehicle Fee</FormLabel>
                    <RadioGroup value={vehicleFeeId} onChange={handleVehicleFeeChange}>
                      <FormControlLabel value="none" control={<Radio />} label="No vehicle" />
                      {activeVehicleFees.map((fee) => (
                        <FormControlLabel
                          key={fee.id}
                          value={String(fee.id)}
                          control={<Radio />}
                          label={`${fee.vehicle_name}${fee.route_name ? ` - ${fee.route_name}` : ''} (${Number(fee.amount).toFixed(2)})`}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                  {vehicleFeeId !== 'none' && (
                    <TextField
                      label="Selected Vehicle Amount"
                      type="number"
                      value={vehicleFeeAmount}
                      onChange={(event) => setVehicleFeeAmount(event.target.value)}
                      disabled={!feesEditable}
                      fullWidth
                      sx={{ mt: 2 }}
                      inputProps={{ min: 0, step: '0.01' }}
                    />
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  Total admission fee: {feeTotal.toFixed(2)}
                </Alert>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Saving...' : 'Save Student'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default AdminStudents;
